import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Music, RefreshCw, ExternalLink, Unplug, LinkIcon, Sparkles, Search, Copy, AlertCircle, Trash2, Link2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const StatusBadge = ({ status }) => {
  const meta = {
    matched: { label: "Matched", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    already_synced: { label: "In playlist", tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    not_found: { label: "Not found", tone: "text-amber-800 bg-amber-50 border-amber-200" },
    pending: { label: "Pending sync", tone: "text-foreground/70 bg-foreground/5 border-border" },
  }[status] || { label: "Pending sync", tone: "text-foreground/70 bg-foreground/5 border-border" };
  return (
    <span className={"inline-flex items-center gap-1 h-6 px-2 rounded-full border text-[10px] tracking-[0.14em] uppercase " + meta.tone}>
      {meta.label}
    </span>
  );
};

/**
 * BindPlaylistCard
 *
 * Lets the admin point the sync at an EXISTING Spotify playlist (paste its
 * link/URI/ID) instead of the auto-created one — so pre-existing tracks
 * (e.g. Bee Gees added by Elias) stay put and future guest requests join them.
 */
const BindPlaylistCard = ({ status, onBound }) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const bound = Boolean(status?.playlist_bound);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!value.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/spotify/bind-playlist", { playlist: value.trim() });
      toast.success(`Bound to "${data.playlist_name}" — future syncs will add to this playlist.`);
      setValue("");
      onBound?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't bind that playlist.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-foreground/[0.02] p-4" data-testid="admin-songs-bind-playlist">
      <p className="editorial-kicker inline-flex items-center gap-2 text-foreground/70">
        <Link2 className="w-3.5 h-3.5 text-accent" />
        {bound ? "Playlist bound" : "Use an existing playlist"}
      </p>
      <p className="mt-1 text-xs text-foreground/55 leading-relaxed max-w-[62ch]">
        {bound
          ? "Currently syncing into your existing Spotify playlist. Paste a different link below to change it."
          : "Paste a Spotify playlist link (or URI/ID). Future guest requests will be added to that playlist and existing tracks stay put."}
      </p>
      <form onSubmit={submit} className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="https://open.spotify.com/playlist/…"
          className="flex-1 min-w-[240px] font-mono text-xs h-10"
          data-testid="admin-songs-bind-input"
          disabled={busy}
        />
        <Button
          type="submit"
          disabled={busy || !value.trim()}
          className="h-10 px-4 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-[11px] tracking-[0.18em] uppercase"
          data-testid="admin-songs-bind-submit"
        >
          {busy ? "Binding…" : bound ? "Rebind" : "Bind playlist"}
        </Button>
      </form>
    </div>
  );
};

/**
 * SpotifyPlaylistPanel
 *
 * Live view of the current wedding Spotify playlist with:
 *  - per-track Remove button (also clears our song_syncs memory so guests can re-request)
 *  - a "Sync after Spotify edits" button that reconciles our DB after you've
 *    manually removed tracks in the Spotify app.
 */
const SpotifyPlaylistPanel = ({ connected }) => {
  const [tracks, setTracks] = useState([]);
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [pending, setPending] = useState(null); // track we're about to delete
  const [scopeStale, setScopeStale] = useState(false);

  const load = useCallback(async () => {
    if (!connected) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/admin/dj/playlist");
      setTracks(data.tracks || []);
      setPlaylistUrl(data.playlist_url || "");
      setScopeStale(data.reason === "insufficient_scope");
    } catch (_e) {
      toast.error("Couldn't load playlist");
    } finally {
      setLoading(false);
    }
  }, [connected]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    setBusyId(t.id);
    try {
      const { data } = await api.delete(`/admin/dj/playlist/${t.id}`);
      setTracks((arr) => arr.filter((x) => x.id !== t.id));
      toast.success(`Removed · guests can re-request${data.cleared_sync_records ? ` (${data.cleared_sync_records} memory cleared)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setBusyId(null);
      setPending(null);
    }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/dj/reconcile");
      const cleared = data.stale_records_cleared || 0;
      const list = (data.cleared_songs || []).filter(Boolean);
      if (cleared === 0) {
        toast.success("Already in sync · nothing to clear");
      } else {
        toast.success(`Cleared ${cleared} stale ${cleared === 1 ? "entry" : "entries"}${list.length ? " · " + list.slice(0, 3).join(", ") + (list.length > 3 ? "…" : "") : ""}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  if (!connected) return null;

  return (
    <div className="surface-card p-5" data-testid="admin-dj-playlist-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="editorial-kicker inline-flex items-center gap-2 text-foreground/70">
            <Music className="w-3.5 h-3.5 text-accent" />
            Wedding playlist ({tracks.length})
          </p>
          <p className="text-xs text-foreground/55 mt-1 max-w-[60ch]">
            Live from Spotify. Remove a track here to also clear its memory so guests can re-request it.
            If you removed songs manually in Spotify, tap <b>Sync after Spotify edits</b> to update the memory.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reconcile}
            disabled={reconciling}
            data-testid="admin-dj-reconcile"
            className="text-[11px] tracking-[0.16em] uppercase"
          >
            <RefreshCw className={"w-3.5 h-3.5 mr-1.5 " + (reconciling ? "animate-spin" : "")} />
            {reconciling ? "Syncing…" : "Sync after Spotify edits"}
          </Button>
          {playlistUrl && (
            <a
              href={playlistUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border hover:border-accent/60 hover:text-accent text-[11px] tracking-[0.16em] uppercase text-foreground/70"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open
            </a>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center editorial-kicker text-foreground/50">Loading playlist…</div>
      ) : scopeStale ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="admin-dj-scope-stale">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Spotify needs a fresh connection.</p>
            <p className="mt-1 text-xs">
              The current token doesn&rsquo;t have permission to list or edit the playlist.
              Click <b>Disconnect</b> and then <b>Connect Spotify</b> above to grant the newer playlist scopes.
            </p>
          </div>
        </div>
      ) : tracks.length === 0 ? (
        <div className="py-8 text-center editorial-kicker text-foreground/50">Playlist is empty (or Spotify is off).</div>
      ) : (
        <div className="divide-y divide-border/60 max-h-[520px] overflow-y-auto rounded border border-border/60">
          {tracks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3 hover:bg-foreground/[0.02]" data-testid={`admin-dj-track-${t.id}`}>
              {t.cover ? (
                <img src={t.cover} alt="" className="w-10 h-10 rounded shrink-0 object-cover" />
              ) : (
                <div className="w-10 h-10 rounded bg-foreground/10 inline-flex items-center justify-center shrink-0">
                  <Music className="w-4 h-4 text-foreground/40" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{t.name || "Unknown"}</p>
                <p className="text-xs text-foreground/60 truncate">
                  {t.artist || "Unknown artist"}
                  {t.requested_by && <span className="text-foreground/40"> · requested by {t.requested_by}</span>}
                </p>
              </div>
              {t.url && (
                <a
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] tracking-[0.14em] uppercase text-foreground/50 hover:text-accent px-2"
                  title="Open in Spotify"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              <button
                type="button"
                onClick={() => setPending(t)}
                disabled={busyId === t.id}
                data-testid={`admin-dj-remove-${t.id}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 text-[11px] tracking-[0.14em] uppercase disabled:opacity-60"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from playlist?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pending?.name}</b> — {pending?.artist}
              <br />
              This deletes the track from your Spotify playlist and clears the request memory so guests can re-request it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pending && remove(pending)}
              className="bg-destructive text-background hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const AdminSongs = () => {
  const [status, setStatus] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(null); // rsvp_id being deleted
  const [pendingDelete, setPendingDelete] = useState(null); // {id, name, song}

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/spotify/status");
      setStatus(data);
    } catch (_e) {
      // ignore
    }
  }, []);

  const loadSongs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/spotify/songs");
      setSongs(data.songs || []);
    } catch (_e) {
      toast.error("Failed to load songs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadSongs();
    // If we just came back from Spotify OAuth, celebrate + clean the URL.
    const params = new URLSearchParams(window.location.search);
    if (params.get("spotify") === "connected") {
      toast.success("Spotify connected. Ready to sync song requests.");
      params.delete("spotify");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [loadStatus, loadSongs]);

  const onConnect = async () => {
    try {
      const { data } = await api.get("/admin/spotify/authorize");
      // Redirect the whole tab — popups get blocked and lose the OAuth state.
      window.location.href = data.auth_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("not configured")) {
        toast.error("Spotify Client ID/Secret aren't set on the server. Ask Emergent support to add them.");
      } else {
        toast.error("Couldn't start Spotify connect.");
      }
    }
  };

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(status?.redirect_uri || "");
      toast.success("Callback URL copied");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect Spotify? The playlist stays on your account; new RSVPs just won't auto-sync until you reconnect.")) return;
    try {
      await api.post("/admin/spotify/disconnect");
      toast.success("Spotify disconnected.");
      await loadStatus();
    } catch (_e) {
      toast.error("Disconnect failed.");
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/spotify/sync");
      toast.success(`Added ${data.added} songs · ${data.already_synced} already there · ${data.not_found} not found.`);
      await Promise.all([loadStatus(), loadSongs()]);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Sync failed.";
      toast.error(detail, { duration: 8000 });
      console.error("Spotify sync error:", e?.response?.data || e);
    } finally {
      setSyncing(false);
    }
  };

  const onDeleteConfirm = async () => {
    if (!pendingDelete) return;
    const { id, song } = pendingDelete;
    setDeleting(id);
    try {
      const { data } = await api.delete(`/admin/spotify/songs/${id}`);
      if (data.removed_from_playlist) {
        toast.success(`"${song}" removed from playlist.`);
      } else if (data.note) {
        toast.message(`Deleted locally. ${data.note}`, { duration: 6000 });
      } else {
        toast.success(`"${song}" deleted.`);
      }
      await loadSongs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeleting(null);
      setPendingDelete(null);
    }
  };

  const connected = status?.connected;

  return (
    <div className="space-y-8" data-testid="admin-songs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="editorial-kicker">Song requests · Spotify</p>
          <p className="text-sm text-foreground/60 mt-1 max-w-[62ch]">
            Guests can request a song on the RSVP form. Connect Spotify once and hit &ldquo;Sync&rdquo; &mdash; we search each
            request and add the top match to a private playlist on your account. Duplicates and already-added
            songs are skipped.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadSongs} variant="outline" className="h-10 px-4 rounded-xl text-[12px] tracking-[0.18em] uppercase" data-testid="admin-songs-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          {connected ? (
            <>
              <Button onClick={onSync} disabled={syncing} className="h-10 px-4 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.18em] uppercase" data-testid="admin-songs-sync">
                <Sparkles className="w-4 h-4 mr-2" /> {syncing ? "Syncing…" : "Sync now"}
              </Button>
              <Button onClick={onDisconnect} variant="outline" className="h-10 px-4 rounded-xl text-[12px] tracking-[0.18em] uppercase text-destructive/85 border-destructive/40 hover:bg-destructive/10 hover:text-destructive" data-testid="admin-songs-disconnect">
                <Unplug className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </>
          ) : (
            <Button onClick={onConnect} className="h-10 px-4 rounded-xl bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.18em] uppercase" data-testid="admin-songs-connect">
              <LinkIcon className="w-4 h-4 mr-2" /> Connect Spotify
            </Button>
          )}
        </div>
      </div>

      {/* Connection card */}
      <div className="surface-card p-5" data-testid="admin-songs-status">
        <p className="editorial-kicker mb-2">Connection</p>
        {connected ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Connected as <b>{status.user?.display_name || status.user?.id}</b>
              </span>
              <span className="text-foreground/60">Playlist: <b>{status.playlist_name}</b></span>
              {status.playlist_url ? (
                <a href={status.playlist_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline" data-testid="admin-songs-playlist-link">
                  Open playlist <ExternalLink className="w-3.5 h-3.5" />
                </a>
              ) : (
                <span className="text-foreground/50 text-xs italic">Playlist appears after the first successful sync.</span>
              )}
            </div>
            {status.user_email && (
              <div className="rounded-lg border border-border bg-foreground/[0.02] p-3 text-xs" data-testid="admin-songs-user-email">
                <span className="editorial-kicker text-foreground/55">Spotify email · </span>
                <code className="font-mono text-foreground/85">{status.user_email}</code>
                <p className="mt-2 text-foreground/55 leading-relaxed">
                  If Sync returns 403 Forbidden, this <b>exact</b> email must be added in your Spotify Developer app under <b>User Management</b>. Facebook-linked Spotify accounts have a hidden email — this is the one Spotify actually uses.
                </p>
                <p className="mt-2 text-foreground/55 leading-relaxed">
                  <b>Live &ldquo;now playing&rdquo; widget:</b> if the DJ page shows nothing while you play a song, click <b>Disconnect</b> then <b>Connect Spotify</b> again to grant the newer read-playback scopes.
                </p>
              </div>
            )}
            <BindPlaylistCard status={status} onBound={loadStatus} />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-foreground/75">
              Not connected. Click <b>Connect Spotify</b> above and grant playlist access — this is a one-time step.
            </p>
            {status?.client_configured === false && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="admin-songs-config-missing">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Spotify credentials not set on the server.</p>
                  <p className="mt-1 text-xs">The <code className="font-mono">SPOTIFY_CLIENT_ID</code> and <code className="font-mono">SPOTIFY_CLIENT_SECRET</code> env vars aren&rsquo;t configured. Add them in your Emergent env settings and redeploy.</p>
                </div>
              </div>
            )}
            {status?.redirect_uri && (
              <div className="rounded-lg border border-border bg-foreground/[0.02] p-4" data-testid="admin-songs-redirect-uri">
                <p className="editorial-kicker text-foreground/55">Redirect URI to whitelist in Spotify</p>
                <p className="mt-2 text-xs text-foreground/65 leading-relaxed">
                  If clicking Connect goes to a Spotify error page (e.g. <i>&ldquo;INVALID_CLIENT: Invalid redirect URI&rdquo;</i>), open your Spotify app at{" "}
                  <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">developer.spotify.com/dashboard</a>
                  {" "}→ <b>Edit settings</b> → <b>Redirect URIs</b>, and paste the URL below.
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <code className="font-mono text-xs bg-foreground/5 px-3 py-2 rounded border border-border break-all flex-1 min-w-0">
                    {status.redirect_uri}
                  </code>
                  <button
                    type="button"
                    onClick={copyRedirect}
                    data-testid="admin-songs-copy-redirect"
                    className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-border hover:border-foreground/40 text-foreground/70 hover:text-foreground text-[11px] tracking-[0.18em] uppercase transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </div>
                <p className="mt-3 text-xs text-foreground/55">
                  <b>Tip:</b> whitelist <i>both</i> the preview URL and your production URL{" "}
                  (<code className="font-mono text-[11px]">https://eliandmag.wedding/api/spotify/callback</code>) so the connect flow works in either place.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Live Spotify playlist — remove tracks + reconcile after manual Spotify edits */}
      <SpotifyPlaylistPanel connected={connected} />

      {/* Songs table */}
      <div className="surface-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center editorial-kicker">Loading…</div>
        ) : songs.length === 0 ? (
          <div className="p-10 text-center editorial-kicker">No song requests yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-songs-table">
              <thead>
                <tr className="bg-foreground/5 text-[10px] tracking-[0.22em] uppercase text-foreground/60">
                  <th className="text-left px-4 py-3">Requested by</th>
                  <th className="text-left px-4 py-3">Song</th>
                  <th className="text-left px-4 py-3">Spotify match</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {songs.map((s, i) => {
                  const sync = s.sync;
                  return (
                    <tr key={s.id || i} className="border-t border-border/60" data-testid={`admin-song-row-${s.id || i}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{s.name}</div>
                        <div className="text-xs text-foreground/50">{s.language?.toUpperCase()}</div>
                      </td>
                      <td className="px-4 py-3 font-serif-display italic">{s.song_request}</td>
                      <td className="px-4 py-3">
                        {(sync?.status === "matched" || sync?.status === "already_synced") ? (
                          <div className="flex items-center gap-3">
                            {sync.cover && <img src={sync.cover} alt="" className="w-10 h-10 rounded" />}
                            <div className="min-w-0">
                              <div className="font-medium truncate">{sync.track_name}</div>
                              <div className="text-xs text-foreground/60 truncate">{sync.artist}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-foreground/40">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={sync?.status || "pending"} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-3 justify-end">
                          {sync?.spotify_url ? (
                            <a href={sync.spotify_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline text-xs" data-testid={`admin-song-open-${s.id || i}`}>
                              Open <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <a href={`https://open.spotify.com/search/${encodeURIComponent(s.song_request)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-foreground/60 hover:text-foreground text-xs" data-testid={`admin-song-search-${s.id || i}`}>
                              <Search className="w-3 h-3" /> Search
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => setPendingDelete({ id: s.id, name: s.name, song: s.song_request, hasMatch: Boolean(sync?.track_uri) })}
                            disabled={deleting === s.id}
                            data-testid={`admin-song-delete-${s.id || i}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-foreground/40 hover:text-destructive hover:bg-destructive/5 border border-transparent hover:border-destructive/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            aria-label={`Delete song request from ${s.name}`}
                            title="Delete song request"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="admin-song-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this song request?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  <span className="block mb-2">
                    <b className="font-serif-display italic text-foreground">&ldquo;{pendingDelete.song}&rdquo;</b> requested by <b>{pendingDelete.name}</b>.
                  </span>
                  {pendingDelete.hasMatch ? (
                    <span className="block text-foreground/70">
                      It will be removed from the Spotify playlist and cleared from {pendingDelete.name}&rsquo;s RSVP so it won&rsquo;t re-sync.
                    </span>
                  ) : (
                    <span className="block text-foreground/70">
                      It will be cleared from {pendingDelete.name}&rsquo;s RSVP so it won&rsquo;t sync later.
                    </span>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-song-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteConfirm}
              data-testid="admin-song-delete-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove song
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminSongs;
